import { useId } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Which field the generated cards come out ordered by — PDF page order, and
 * the order entries are written to a zip.
 *
 * A select rather than the segmented buttons the other options use: the
 * candidates are the chosen template's own variables, so there can be a dozen
 * of them and their names are as long as the template says.
 *
 * With no fields it renders disabled and offers nothing, so the option stays
 * visible (and its place in the form stable) without pretending there is
 * anything to choose yet. Two different situations land there — no template at
 * all, or a template that renders no variable — and the hint says which.
 */
export function SortByChoice({
  fields,
  hasTemplate,
  value,
  onPick,
}: {
  /** The fields this batch can be ordered by; the template's own variables. */
  fields: string[];
  /** Whether a template is selected — which of the two empty states this is. */
  hasTemplate: boolean;
  /** The chosen field; '' leaves the order to the pipeline. */
  value: string;
  onPick: (field: string) => void;
}) {
  const { t } = useTranslation();
  const disabled = fields.length === 0;
  const selectId = useId();

  /** These two read better named than raw, when a template does render them. */
  const labelFor = (field: string): string => {
    if (field === 'upn') {
      return t('cardGenerator.sortBy.upn');
    }
    if (field === 'displayName') {
      return t('cardGenerator.sortBy.displayName');
    }
    return field;
  };

  const hintKey = (): string => {
    if (!hasTemplate) {
      return 'cardGenerator.sortBy.needsTemplate';
    }
    if (disabled) {
      return 'cardGenerator.sortBy.noFields';
    }
    return 'cardGenerator.sortBy.hint';
  };

  return (
    <div>
      <label
        htmlFor={selectId}
        className="mb-1 block text-xs font-medium"
        style={{ color: disabled ? 'var(--muted)' : 'var(--ink)' }}
      >
        {t('cardGenerator.sortBy.label')}
      </label>
      <select
        id={selectId}
        value={value}
        disabled={disabled}
        onChange={(e) => onPick(e.target.value)}
        className="w-full px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
      >
        {/* The empty value's own label, not a field: a select whose value has
        no matching option renders blank. */}
        <option value="">{t('cardGenerator.sortBy.none')}</option>
        {fields.map((field) => (
          <option key={field} value={field}>
            {labelFor(field)}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-muted">{t(hintKey())}</p>
    </div>
  );
}
